// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchProfileImage, fetchTeacherSubjectsWithStudents, normalizeProfileImageFilename, uploadProfileImage, fetchTeacherStudentParents, addTeacherStudentParent, updateTeacherStudentParent, removeTeacherStudentParent } from '../../services/api';
import BackToTop from '../../components/BackToTop';
import FamilyMembersEditor from '../../components/FamilyMembersEditor';
import { useAuth } from '../../context/AuthContext';
import { isReadOnlyTeacherDemo } from '../../utils/demoAccess';
import IreadyScoreBoxes from '../../components/IreadyScoreBoxes';
import StudentNameOrderToggle from '../../components/StudentNameOrderToggle';
import { sortStudentsByName, useStudentNameOrder } from '../../utils/studentSort';

const gradeOrder = (grade) => {
  const value = String(grade || '').toUpperCase();
  if (value === 'PK') return -2;
  if (value === 'K') return -1;
  const n = Number(value);
  return Number.isNaN(n) ? 999 : n;
};

const getStudentLabel = (student) => {
  return `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`;
};

const toEnrollmentKey = (enrollment) => `${enrollment.subject_id || enrollment.subject_name}:${enrollment.class_id}`;

const getInitials = (student) => {
  const first = String(student.first_name || '').trim();
  const last = String(student.last_name || '').trim();
  const fallback = String(student.student_id || '?');
  return `${first[0] || fallback[0] || '?'}${last[0] || ''}`.toUpperCase();
};

const mergeFamilyMembers = (existing = [], incoming = []) => {
  const byKey = new Map();
  [...existing, ...incoming].forEach((member) => {
    const key = String(member?.email || member?.user_id || member?.parent_id || '').toLowerCase();
    if (!key) return;
    byKey.set(key, member);
  });
  return Array.from(byKey.values());
};

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read image file.'));
    reader.readAsDataURL(file);
  });
}

function applySubjectAvatarMap(subjects, setAvatarMap) {
  setAvatarMap((prev) => {
    const next = { ...prev };
    (subjects || []).forEach((subject) => {
      (subject.classes || []).forEach((cls) => {
        (cls.students || []).forEach((student) => {
          if (student.avatar_url || student.avatar_filename) {
            next[student.student_id] = {
              ...next[student.student_id],
              remoteFilename: normalizeProfileImageFilename(student.avatar_filename || student.avatar_url) || next[student.student_id]?.remoteFilename || '',
            };
          }
        });
      });
    });
    return next;
  });
}

export default function StudentProfiles() {
  const { user } = useAuth();
  const readOnlyDemo = isReadOnlyTeacherDemo(user);
  const navigate = useNavigate();
  const { classId, subject: subjectParam } = useParams();
  const decodedSubject = subjectParam ? decodeURIComponent(subjectParam) : '';

  const [subjects, setSubjects] = useState<any>([]);
  const [nameOrder] = useStudentNameOrder();
  const [loading, setLoading] = useState<any>(true);
  const [refreshing, setRefreshing] = useState<any>(false);
  const [error, setError] = useState<any>('');

  const [selectedStudentId, setSelectedStudentId] = useState<any>(null);

  const [searchQuery, setSearchQuery] = useState<any>('');
  const [selectedGrade, setSelectedGrade] = useState<any>('');

  const [avatarMap, setAvatarMap] = useState<any>({});
  const [avatarUploadingFor, setAvatarUploadingFor] = useState<any>(null);
  const [avatarMessage, setAvatarMessage] = useState<any>('');

  const loadSubjects = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const data = await fetchTeacherSubjectsWithStudents();
      const nextSubjects = data.subjects || [];
      setSubjects(nextSubjects);
      applySubjectAvatarMap(nextSubjects, setAvatarMap);
    } catch (_err: any) {
      setError('Could not load students.');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSubjects(false);
  }, []);

  const allStudents = useMemo(() => {
    const studentMap = new Map();

    subjects.forEach((subject) => {
      const subjectName = subject.subject_name || 'Subject';
      const subjectId = subject.subject_id;

      if (decodedSubject && subjectName !== decodedSubject) {
        return;
      }

      (subject.classes || []).forEach((cls) => {
        if (classId && String(cls.class_id) !== String(classId)) {
          return;
        }

        (cls.students || []).forEach((student) => {
          const studentId = student.student_id;
          if (!studentMap.has(studentId)) {
            studentMap.set(studentId, {
              student_id: studentId,
              user_id: student.user_id,
              first_name: student.first_name,
              last_name: student.last_name,
              grade_level: student.grade_level,
                avatar_filename: student.avatar_filename,
                avatar_url: student.avatar_url,
              family_members: student.family_members || [],
              enrollments: [],
            });
          }

          const entry = studentMap.get(studentId);
          entry.avatar_filename = entry.avatar_filename || student.avatar_filename;
          entry.avatar_url = entry.avatar_url || student.avatar_url;
          entry.family_members = mergeFamilyMembers(entry.family_members, student.family_members || []);
          const enrollment = {
            class_id: cls.class_id,
            class_name: cls.class_name || `Class ${cls.class_id}`,
            teacher_id: cls.teacher_id,
            teacher_name: cls.teacher_name || '',
            subject_id: subjectId,
            subject_name: subjectName,
            grade_level: student.grade_level,
          };

          const exists = entry.enrollments.some(
            (item) => item.class_id === enrollment.class_id && item.subject_name === enrollment.subject_name
          );

          if (!exists) {
            entry.enrollments.push(enrollment);
          }
        });
      });
    });

    return Array.from(studentMap.values())
      .map((student) => ({
        ...student,
        enrollments: [...student.enrollments].sort((a, b) => {
          const subjectCmp = String(a.subject_name).localeCompare(String(b.subject_name));
          if (subjectCmp !== 0) return subjectCmp;
          return String(a.class_name).localeCompare(String(b.class_name));
        }),
      }));
  }, [subjects, decodedSubject, classId]);

  useEffect(() => {
    const studentsWithSavedAvatars = allStudents.filter((student) => student.avatar_filename || student.avatar_url);
    let cancelled = false;
    const objectUrls = [];
    setAvatarMap((previous) => {
      const next = { ...previous };
      studentsWithSavedAvatars.forEach((student) => {
        next[student.student_id] = { ...next[student.student_id], avatarUrl: '', imageFailed: false };
      });
      return next;
    });

    Promise.all(studentsWithSavedAvatars.map(async (student) => {
      try {
        const blob = await fetchProfileImage(normalizeProfileImageFilename(student.avatar_filename || student.avatar_url));
        const avatarUrl = URL.createObjectURL(blob);
        objectUrls.push(avatarUrl);
        return { studentId: student.student_id, avatarUrl };
      } catch (_err: any) {
        return null;
      }
    })).then((loadedAvatars) => {
      if (cancelled) return;
      setAvatarMap((previous) => {
        const next = { ...previous };
        loadedAvatars.filter(Boolean).forEach(({ studentId, avatarUrl }) => {
          next[studentId] = { ...next[studentId], avatarUrl, imageFailed: false };
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [allStudents]);

  const sortedStudents = useMemo(() => sortStudentsByName(allStudents, nameOrder), [allStudents, nameOrder]);

  const gradeOptions = useMemo(() => {
    const values = new Set();
    allStudents.forEach((student) => {
      if (student.grade_level !== null && student.grade_level !== undefined && String(student.grade_level).trim() !== '') {
        values.add(String(student.grade_level));
      }
    });
    return Array.from(values).sort((a, b) => gradeOrder(a) - gradeOrder(b));
  }, [allStudents]);

  const filteredStudents = useMemo(() => {
    let result = sortedStudents;

    if (selectedGrade) {
      result = result.filter((student) => String(student.grade_level) === selectedGrade);
    }

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((student) => {
        const studentName = getStudentLabel(student).toLowerCase();
        const subjectMatch = student.enrollments.some((item) => String(item.subject_name).toLowerCase().includes(query));
        return studentName.includes(query) || subjectMatch;
      });
    }

    return result;
  }, [sortedStudents, selectedGrade, searchQuery]);

  useEffect(() => {
    if (!filteredStudents.length) {
      setSelectedStudentId(null);
      return;
    }

    if (!selectedStudentId || !filteredStudents.some((student) => String(student.student_id) === String(selectedStudentId))) {
      setSelectedStudentId(filteredStudents[0].student_id);
    }
  }, [filteredStudents, selectedStudentId]);

  const selectedStudent = useMemo(() => {
    return filteredStudents.find((student) => String(student.student_id) === String(selectedStudentId)) || null;
  }, [filteredStudents, selectedStudentId]);

  const uploadAvatar = async (student, file) => {
    if (!student || !file) return;

    if (!file.type.startsWith('image/')) {
      setAvatarMessage('Please upload an image file (PNG or JPG).');
      return;
    }

    setAvatarUploadingFor(student.student_id);
    setAvatarMessage('');

    try {
      const previewDataUrl = await readFileAsDataUrl(file);

      let remoteFilename = '';
      try {
        const result = await uploadProfileImage(student.student_id, file);
        remoteFilename = result?.avatar_filename || result?.filename || result?.file_name || '';
      } catch (_err: any) {
        // Keep local preview even when remote upload fails.
      }

      setAvatarMap((prev) => ({
        ...prev,
        [student.student_id]: {
          previewDataUrl,
          remoteFilename,
          avatarUrl: '',
          imageFailed: false,
          updatedAt: Date.now(),
        },
      }));
      setAvatarMessage('Profile avatar updated.');
    } catch (_err: any) {
      setAvatarMessage('Could not upload this image. Please try another file.');
    } finally {
      setAvatarUploadingFor(null);
    }
  };

  const markAvatarFailed = (studentId) => {
    setAvatarMap((previous) => ({
      ...previous,
      [studentId]: { ...previous[studentId], previewDataUrl: '', avatarUrl: '', imageFailed: true },
    }));
  };

  // Route straight into the student's progress view (all subjects); the subject
  // switcher there replaces the old per-subject button list on this page.
  const openStudentProgressionAllSubjects = (student) => {
    if (!student) return;
    navigate(`/teacher/student-progress/${student.student_id}`, {
      state: {
        student,
        subject: student.enrollments[0]?.subject_name || '',
        profileClassId: classId || student.enrollments[0]?.class_id,
        returnTo: {
          pathname: '/teacher/student-profiles',
          label: '← Back to Student Profiles',
        },
      },
    });
  };

  // Lets a teacher preview the same student exactly as the family/parent account would see it.
  const openStudentProgressionAsParent = (student) => {
    if (!student) return;
    navigate(`/teacher/student-progress-family/${student.student_id}`, {
      state: {
        student,
        returnTo: {
          pathname: '/teacher/student-profiles',
          label: '← Back to Student Profiles',
          state: { selectedStudentId: student.student_id },
        },
      },
    });
  };

  const panelStyle = {
    background: 'var(--ae-surface)',
    border: '1px solid var(--ae-border)',
    borderRadius: 14,
    boxShadow: 'var(--ae-shadow-card)',
  };

  const selectedStudentTeachers = Array.from(new Map(
    (selectedStudent?.enrollments || [])
      .filter((item) => item.teacher_name)
      .map((item) => [item.teacher_id || item.teacher_name, item.teacher_name]),
  ).values());

  if (loading) {
    return <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Loading student profiles...</div>;
  }

  if (!allStudents.length) {
    return (
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e3a5f', marginBottom: 10 }}>Student Profiles</h1>
        <p style={{ color: '#64748b', marginTop: 0, marginBottom: 16 }}>
          Select a student profile, then choose a subject button to open proficiency progressions.
        </p>
        <div style={{ ...panelStyle, padding: 20, color: '#6b7280' }}>
          {error || 'No students found for your current classes.'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>Student Profiles</h1>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 14 }}>
        Select a student, then click a subject button to open that student's proficiency progression.
      </p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by student or subject..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          style={{
            flex: '1 1 320px',
            minWidth: 260,
            padding: '9px 11px',
            border: '1px solid #dbe4f0',
            borderRadius: 10,
            fontSize: 13,
            boxSizing: 'border-box',
            background: '#ffffff',
          }}
        />
        <select
          aria-label="Filter by grade"
          value={selectedGrade}
          onChange={(event) => setSelectedGrade(event.target.value)}
          style={{
            minWidth: 140,
            padding: '9px 11px',
            border: '1px solid #dbe4f0',
            borderRadius: 10,
            fontSize: 13,
            background: '#ffffff',
          }}
        >
          <option value="">All Grades</option>
          {gradeOptions.map((grade) => (
            <option key={grade} value={grade}>Grade {grade}</option>
          ))}
        </select>
        <StudentNameOrderToggle />
        <button
          type="button"
          onClick={() => {
            setSearchQuery('');
            setSelectedGrade('');
          }}
          style={{
            background: '#f1f5f9',
            border: '1px solid #d1d9e6',
            borderRadius: 10,
            padding: '9px 12px',
            fontSize: 12,
            fontWeight: 700,
            color: '#334155',
            cursor: 'pointer',
          }}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => loadSubjects(true)}
          disabled={refreshing}
          style={{
            background: '#ffffff',
            border: '1px solid #d1d9e6',
            borderRadius: 10,
            padding: '9px 12px',
            fontSize: 12,
            fontWeight: 700,
            color: '#1e3a5f',
            cursor: refreshing ? 'default' : 'pointer',
            opacity: refreshing ? 0.7 : 1,
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 10, color: '#b91c1c', fontWeight: 600, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(340px, 0.8fr)', gap: 16, minHeight: 420 }}>
        <section style={{ ...panelStyle, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1e3a5f' }}>My Students</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                Click a student card to open their progress page.
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
            Showing {filteredStudents.length} {filteredStudents.length === 1 ? 'student' : 'students'}
          </div>
          </div>

          <div style={{ maxHeight: 560, overflowY: 'auto', paddingRight: 2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(145px, 1fr))', gap: 12 }}>
            {filteredStudents.map((student) => {
              const isActive = String(student.student_id) === String(selectedStudentId);
              const avatar = avatarMap[student.student_id]?.imageFailed ? '' : (avatarMap[student.student_id]?.previewDataUrl || avatarMap[student.student_id]?.avatarUrl);
              return (
                <div
                  key={student.student_id}
                  onClick={() => setSelectedStudentId(student.student_id)}
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    border: isActive ? '1px solid #3f67b7' : '1px solid #dbe4f0',
                    background: isActive ? 'linear-gradient(180deg, #fff0e3 0%, #ffd9bb 100%)' : '#ffffff',
                    borderRadius: 16,
                    padding: 14,
                    cursor: 'pointer',
                    boxShadow: 'var(--ae-shadow-card)',
                    minHeight: 168,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 78,
                        height: 78,
                        borderRadius: '50%',
                        background: '#e0ecff',
                        color: '#1d4ed8',
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '1px solid #bfdbfe',
                        flex: '0 0 auto',
                      }}
                    >
                      {avatar ? (
                        <img src={avatar} alt={`${getStudentLabel(student)} avatar`} onError={() => markAvatarFailed(student.student_id)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span>{getInitials(student)}</span>
                      )}
                    </div>
                    <div style={{ minWidth: 0, width: '100%' }}>
                      <div style={{ fontWeight: 700, color: '#1e3a5f', fontSize: 14, lineHeight: 1.25 }}>{getStudentLabel(student)}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        Grade {student.grade_level ?? 'N/A'}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: isActive ? '#9a3412' : '#64748b' }}>
                        {student.enrollments.length} subject{student.enrollments.length === 1 ? '' : 's'}
                      </div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedStudentId(student.student_id);
                          openStudentProgressionAllSubjects(student);
                        }}
                        style={{
                          marginTop: 8,
                          width: '100%',
                          background: '#1d4ed8',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '7px 10px',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Open Student Progress
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedStudentId(student.student_id);
                          openStudentProgressionAsParent(student);
                        }}
                        style={{
                          marginTop: 6,
                          width: '100%',
                          background: '#f8fafc',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                          borderRadius: 8,
                          padding: '7px 10px',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        View as Family
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        </section>

        <section style={{ ...panelStyle, padding: 16 }}>
          {!selectedStudent ? (
            <div style={{ color: '#6b7280' }}>Select a student to view subjects and progression options.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 18, alignItems: 'start', marginBottom: 16 }}>
                <div
                  style={{
                    width: 120,
                    minHeight: 120,
                    borderRadius: 16,
                    background: 'linear-gradient(180deg, #ffe0c6 0%, #ffd1ad 100%)',
                    border: '1px solid #ffc089',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#1d4ed8',
                    fontWeight: 800,
                    fontSize: 24,
                    padding: 10,
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dbeafe', border: '1px solid #bfdbfe', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {avatarMap[selectedStudent.student_id]?.previewDataUrl ? (
                    <img
                      src={avatarMap[selectedStudent.student_id]?.previewDataUrl}
                      alt={`${getStudentLabel(selectedStudent)} avatar`}
                      onError={() => markAvatarFailed(selectedStudent.student_id)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : avatarMap[selectedStudent.student_id]?.avatarUrl ? (
                    <img
                      src={avatarMap[selectedStudent.student_id]?.avatarUrl}
                      alt={`${getStudentLabel(selectedStudent)} avatar`}
                      onError={() => markAvatarFailed(selectedStudent.student_id)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span>{getInitials(selectedStudent)}</span>
                  )}
                  </div>
                  <div style={{ marginTop: 10, width: '100%', fontSize: 11, lineHeight: 1.35, color: '#9a3412', textAlign: 'left' }}>
                    <div style={{ fontWeight: 800, color: '#1e3a5f' }}>{getStudentLabel(selectedStudent)}</div>
                    <div>Grade {selectedStudent.grade_level ?? 'N/A'}</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  <div style={{ padding: '12px 14px', borderRadius: 14, background: '#ffffff', border: '1px solid #dbe4f0', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.07)' }}>
                    <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 800, color: '#64748b', marginBottom: 6 }}>Student Profile</div>
                    <h2 style={{ margin: '0 0 6px', fontSize: 22, color: '#1e3a5f' }}>{getStudentLabel(selectedStudent)}</h2>
                    <div style={{ fontSize: 13, color: '#64748b', paddingBottom: 8, borderBottom: '1px solid #e5edf7' }}>
                      ID: {selectedStudent.student_id} • Grade: {selectedStudent.grade_level ?? 'N/A'}
                    </div>
                    {!!selectedStudentTeachers.length && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
                        <strong style={{ color: '#1e3a5f' }}>Teachers:</strong> {selectedStudentTeachers.join(', ')}
                      </div>
                    )}
                    <IreadyScoreBoxes studentId={selectedStudent.student_id} editable={!readOnlyDemo} />
                    {!readOnlyDemo && <FamilyMembersEditor
                      studentId={selectedStudent.student_id}
                      fetchParents={fetchTeacherStudentParents}
                      addParent={addTeacherStudentParent}
                      updateParent={updateTeacherStudentParent}
                      removeParent={removeTeacherStudentParent}
                      onChanged={() => loadSubjects(true)}
                    />}
                  </div>

                  {!readOnlyDemo && <>
                  <label
                    htmlFor={`avatar-upload-${selectedStudent.student_id}`}
                    style={{
                      display: 'inline-block',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      borderRadius: 9,
                      padding: '7px 11px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#334155',
                      cursor: avatarUploadingFor === selectedStudent.student_id ? 'default' : 'pointer',
                      opacity: avatarUploadingFor === selectedStudent.student_id ? 0.7 : 1,
                    }}
                  >
                    {avatarUploadingFor === selectedStudent.student_id ? 'Uploading...' : 'Upload Profile Avatar'}
                  </label>
                  <input
                    id={`avatar-upload-${selectedStudent.student_id}`}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      uploadAvatar(selectedStudent, file);
                      event.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                  </>}
                </div>
              </div>

              {avatarMessage && (
                <div
                  style={{
                    marginBottom: 12,
                    color: avatarMessage.includes('Could not') || avatarMessage.includes('Please') ? '#b91c1c' : '#166534',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {avatarMessage}
                </div>
              )}

              <div
                style={{
                  border: '1px solid #dbe4f0',
                  borderRadius: 12,
                  padding: 14,
                  background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                }}
              >
                <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1e3a5f' }}>Enrolled Subjects</h3>
                <div style={{ marginBottom: 10, fontSize: 12, color: '#64748b' }}>
                  Open this student's progress page to switch between subjects there.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {selectedStudent.enrollments.map((enrollment) => (
                    <span
                      key={toEnrollmentKey(enrollment)}
                      style={{
                        border: '1px solid #cbd5e1',
                        background: '#ffffff',
                        color: '#334155',
                        borderRadius: 999,
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {enrollment.subject_name}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => openStudentProgressionAllSubjects(selectedStudent)}
                  style={{
                    width: '100%',
                    background: '#1d4ed8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '10px 14px',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Open Student Progress
                </button>
                <button
                  type="button"
                  onClick={() => openStudentProgressionAsParent(selectedStudent)}
                  style={{
                    width: '100%',
                    marginTop: 8,
                    background: '#f8fafc',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                    borderRadius: 10,
                    padding: '10px 14px',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  View as Family
                </button>
              </div>
            </>
          )}
        </section>
      </div>
      <BackToTop />
    </div>
  );
}
